import { useState, useEffect, useCallback } from "react";
import { CreditCard, Radio, Receipt, Clock, DollarSign, CheckCircle2, AlertCircle } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

export const BillingTab = ({ currentUser, authToken }) => {
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);

  const fetchPaymentHistory = useCallback(async () => {
    setPaymentHistoryLoading(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      const res = await fetch(`${BACKEND_URL}/api/payments/history`, { credentials: 'omit', headers });
      if (res.ok) { setPaymentHistory((await res.json()).payments || []); }
    } catch (err) { console.error('Failed to fetch payment history:', err); }
    finally { setPaymentHistoryLoading(false); }
  }, [authToken]);

  useEffect(() => { fetchPaymentHistory(); }, [fetchPaymentHistory]);

  return (
  <div className="space-y-6" data-testid="billing-tab">
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">Current Plan</h3>
          <div className="flex items-center gap-3">
            <span className={`text-2xl font-bold ${currentUser.plan === 'premium' ? 'text-yellow-400' : currentUser.plan === 'professional' ? 'text-purple-400' : currentUser.plan === 'admin' ? 'text-red-400' : 'text-blue-400'}`} data-testid="current-plan">{currentUser.plan === 'premium' ? 'Premium' : (currentUser.plan || 'starter').charAt(0).toUpperCase() + (currentUser.plan || 'starter').slice(1)}</span>
            {currentUser.premium_player && <span className="px-2.5 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full font-medium" data-testid="premium-badge">Premium Player ({currentUser.premium_type === 'lifetime' ? 'Lifetime' : 'Monthly'})</span>}
          </div>
        </div>
        <CreditCard className="w-10 h-10 text-white/10" />
      </div>
    </div>
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
      <div className="p-6 border-b border-white/10 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Payment History</h3>
        <span className="text-white/40 text-sm">{paymentHistory.length} transaction{paymentHistory.length !== 1 ? 's' : ''}</span>
      </div>
      {paymentHistoryLoading ? (
        <div className="flex justify-center py-12"><Radio className="w-8 h-8 text-purple-400 animate-pulse" /></div>
      ) : paymentHistory.length === 0 ? (
        <div className="p-12 text-center"><Receipt className="w-12 h-12 text-white/15 mx-auto mb-4" /><h4 className="text-white font-medium mb-2">No Transactions Yet</h4><p className="text-white/40 text-sm">Your payment history will appear here once you make a purchase.</p></div>
      ) : (
        <div className="divide-y divide-white/10" data-testid="payment-history-list">
          {paymentHistory.map((p, i) => (
            <div key={p.order_id || i} className="p-5 flex items-center justify-between hover:bg-white/[0.02] transition" data-testid={`payment-row-${i}`}>
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${p.status === 'completed' || p.status === 'COMPLETED' ? 'bg-green-500/20' : p.status === 'pending' ? 'bg-yellow-500/20' : 'bg-red-500/20'}`}>
                  {p.status === 'completed' || p.status === 'COMPLETED' ? <CheckCircle2 className="w-5 h-5 text-green-400" /> : p.status === 'pending' ? <Clock className="w-5 h-5 text-yellow-400" /> : <AlertCircle className="w-5 h-5 text-red-400" />}
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{p.description || p.plan || 'Payment'}</p>
                  <p className="text-white/40 text-xs">{p.method || 'PayPal'} &middot; {new Date(p.created_at || p.date).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-white font-bold">${p.amount?.toFixed?.(2) || p.amount}</p>
                <p className={`text-xs capitalize ${p.status === 'completed' || p.status === 'COMPLETED' ? 'text-green-400' : p.status === 'pending' ? 'text-yellow-400' : 'text-red-400'}`}>{p.status}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
  );
};
